import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import { useCommonTranslation } from '@/i18n/hooks/useTypedTranslation';

interface LinkModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (text: string, url: string) => void;
  initialText?: string;
  initialUrl?: string;
}

export function LinkModal({ open, onClose, onSubmit, initialText = '', initialUrl = '' }: LinkModalProps) {
  const { t } = useCommonTranslation();
  const [text, setText] = useState(initialText);
  const [url, setUrl] = useState(initialUrl);

  // Update state when modal opens with new initial values
  useEffect(() => {
    if (open) {
      setText(initialText);
      setUrl(initialUrl);
    }
  }, [open, initialText, initialUrl]);

  const handleSubmit = () => {
    if (url.trim() && text.trim()) {
      onSubmit(text.trim(), url.trim());
      setText('');
      setUrl('');
      onClose();
    }
  };

  const handleClose = () => {
    setText('');
    setUrl(initialUrl);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableScrollLock
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2, pt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: 'rgba(20, 184, 166, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LinkIcon sx={{ fontSize: 20, color: '#14B8A6' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Link
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 3 }}>
        {/* Link Text Field */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            Link Text
          </Typography>
          <TextField
            autoFocus
            fullWidth
            placeholder="Click here"
            value={text}
            onChange={(e) => setText(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#14B8A6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#14B8A6',
                  borderWidth: 2,
                },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              mt: 0.75,
              display: 'block',
              color: 'text.secondary',
            }}
          >
            The text that will be clickable
          </Typography>
        </Box>

        {/* URL Field */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            URL
          </Typography>
          <TextField
            fullWidth
            placeholder="https://example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSubmit();
              }
            }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#14B8A6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#14B8A6',
                  borderWidth: 2,
                },
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              mt: 0.75,
              display: 'block',
              color: 'text.secondary',
            }}
          >
            Enter a valid URL starting with http:// or https://
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 1.5,
            px: 2.5,
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!url.trim() || !text.trim()}
          sx={{
            borderRadius: 1.5,
            px: 2.5,
            textTransform: 'none',
            fontWeight: 500,
            bgcolor: '#14B8A6',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#0f766e',
              boxShadow: 'none',
            },
            '&:disabled': {
              bgcolor: 'action.disabledBackground',
            },
          }}
        >
          {t('actions.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LinkModal;
